import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { unwrapCompaniesPage } from "@/lib/companies";
import { Sparkles, Loader2, Plus, ChevronDown, ChevronRight, Filter, Layers, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatLakh, MONEY_UNIT_SHORT } from "@/lib/utils-ros";
import { EditableField } from "@/components/revenueos/EditableField";
import { AddCompanyModal } from "@/components/revenueos/AddCompanyModal";
import { ActionEditor } from "@/components/revenueos/ActionEditor";
import { MeetingLogInline } from "@/components/revenueos/MeetingLog";
import { PartnerAssignmentPanel } from "@/components/revenueos/PartnerAssignment";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { CustomerCard } from "@/components/revenueos/CustomerCard";
import { useAuth, canDeleteUniverseCompany } from "@/lib/auth.jsx";

function showDownstreamSync(sync) {
  if (!sync) return;
  const total = (sync.leads || 0) + (sync.opportunities || 0) + (sync.meetings || 0);
  if (total > 0) {
    const parts = [];
    if (sync.leads) parts.push(`${sync.leads} lead${sync.leads !== 1 ? "s" : ""}`);
    if (sync.opportunities) parts.push(`${sync.opportunities} opp${sync.opportunities !== 1 ? "s" : ""}`);
    if (sync.meetings) parts.push(`${sync.meetings} meeting${sync.meetings !== 1 ? "s" : ""}`);
    toast.success(`Correction synced to ${parts.join(", ")}`);
  }
}

const SIZE_LABEL = {
  msme: "MSME", mid_sized: "Mid-sized", large: "Large", unknown: "?"
};

function titleCase(value) {
  return String(value || "").replaceAll("_", " ").replace(/\b\w/g, char => char.toUpperCase());
}

function ICPBar({ score }) {
  const color = score >= 85 ? "#16A34A" : score >= 70 ? "#E28A23" : "#DC2626";
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 h-1.5 bg-black/[0.06] rounded-sm overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${score}%`, background: color }} />
      </div>
      <span className="text-[12px] text-[#526D7A] tabular-nums w-6 text-right">{score}</span>
    </div>
  );
}

const PAGE_SIZE = 100;

export default function Universe() {
  const { user, canWrite } = useAuth();
  const canEditUniverse = canWrite("universe");
  const canCreateSignal = canWrite("signals");
  const canLogMeetings = canWrite("customers");
  const canDeleteCompany = canDeleteUniverseCompany(user);
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [scoring, setScoring] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sizeFilter, setSizeFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [fitFilter, setFitFilter] = useState("all");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [partners, setPartners] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [customerCid, setCustomerCid] = useState(null);

  const buildParams = useCallback((nextSkip = 0) => {
    const params = { skip: nextSkip, limit: PAGE_SIZE };
    if (statusFilter !== "all") params.status = statusFilter;
    if (sizeFilter !== "all") params.size_category = sizeFilter;
    if (industryFilter !== "all") params.industry = industryFilter;
    if (partnerFilter !== "all") params.partner_id = partnerFilter;
    if (fitFilter !== "all") params.fit = fitFilter;
    const trimmed = q.trim();
    if (trimmed.length >= 2) params.q = trimmed;
    return params;
  }, [statusFilter, sizeFilter, industryFilter, partnerFilter, fitFilter, q]);

  async function load(opts = {}) {
    const silent = opts.silent === true;
    const append = opts.append === true;
    if (!silent && !append) setLoading(true);
    if (append) setLoadingMore(true);
    const nextSkip = append ? companies.length : 0;
    try {
      const { data } = await api.get("/companies", { params: buildParams(nextSkip) });
      const page = unwrapCompaniesPage(data);
      setCompanies(prev => (append ? [...prev, ...page.items] : page.items));
      setTotal(page.total);
      setHasMore(page.hasMore);
    } catch {
      if (!silent && !append) toast.error("Could not load companies");
      if (!append) {
        setCompanies([]);
        setTotal(0);
        setHasMore(false);
      }
    } finally {
      if (!silent && !append) setLoading(false);
      if (append) setLoadingMore(false);
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => { load(); }, 250);
    return () => clearTimeout(timer);
  }, [statusFilter, sizeFilter, industryFilter, partnerFilter, fitFilter, q]); // eslint-disable-line react-hooks/exhaustive-deps

  function patchCompany(cid, patch) {
    setCompanies(prev => prev.map(c => (c.id === cid ? { ...c, ...patch } : c)));
  }

  useEffect(() => {
    api.get("/partner-firms").then(({ data }) => setPartners(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const cid = searchParams.get("company");
    if (!cid) return;

    if (companies.some((c) => c.id === cid)) {
      setExpanded(cid);
      requestAnimationFrame(() => {
        document.getElementById(`company-row-${cid}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
      return;
    }

    if (loading) return;

    let cancelled = false;
    api.get(`/companies/${cid}`)
      .then(({ data }) => {
        if (cancelled || !data.company) return;
        setCompanies((prev) => (prev.some((c) => c.id === data.company.id) ? prev : [data.company, ...prev]));
        setExpanded(cid);
        requestAnimationFrame(() => {
          document.getElementById(`company-row-${cid}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      })
      .catch(() => { if (!cancelled) toast.error("Company not found or not accessible"); });
    return () => { cancelled = true; };
  }, [searchParams, companies, loading]);

  async function rescore(cid) {
    setScoring(cid);
    try {
      const { data } = await api.post(`/companies/${cid}/score-icp`);
      toast.success(`Re-scored — ICP ${data.icp_score}`);
      patchCompany(cid, { icp_score: data.icp_score, ai_summary: data.ai_summary });
    } catch { toast.error("Re-score failed"); }
    finally { setScoring(null); }
  }

  async function deleteCompany(cid, name) {
    if (!window.confirm(`Delete ${name || "this company"} from Universe?\n\nIt will be moved to Lost. Existing Signals, Leads, Opportunities, and Orders will remain intact. You can restore the company using the Lost button on the right side of the pipeline bar.`)) return;
    try {
      await api.delete(`/companies/${cid}?reason=user_deleted`);
      setCompanies(prev => prev.filter(company => company.id !== cid));
      setExpanded(current => current === cid ? null : current);
      toast.success("Company moved to Lost · downstream records preserved");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not delete company");
    }
  }

  const industries = [...new Set(companies.map(c => c.industry).filter(Boolean))].sort();
  const activeFilterCount = [
    sizeFilter !== "all", statusFilter !== "all", partnerFilter !== "all",
    fitFilter !== "all", industryFilter !== "all",
  ].filter(Boolean).length;

  function clearFilters() {
    setSizeFilter("all");
    setStatusFilter("all");
    setPartnerFilter("all");
    setFitFilter("all");
    setIndustryFilter("all");
  }

  const STATUS_TABS = [
    { key: "all", label: "All" }, { key: "research", label: "Research" }, { key: "target", label: "Target" },
    { key: "marketing", label: "Marketing" }, { key: "lead_generated", label: "Lead Gen" }, { key: "active_customer", label: "Customers" },
  ];
  const SIZE_TABS = [
    { key: "all", label: "All sizes" }, { key: "msme", label: "MSME" }, { key: "mid_sized", label: "Mid" }, { key: "large", label: "Large" }
  ];

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="universe-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-6 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">Universe</h1>
        <p className="text-[13px] text-[#FFFFFF]/75 mt-1">Every company Saarv could sell to.</p>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        <PageToolbar
          query={q}
          onQueryChange={setQ}
          placeholder="Search companies by name or industry…"
          entityLabel="companies"
          count={total}
          variant="universe"
          right={(
            <div className="flex items-center gap-2 shrink-0">
              {canEditUniverse && (
              <button onClick={() => setAddOpen(true)} data-testid="add-company-btn"
                      className="h-9 px-3 rounded-lg border border-[#BFD6E1] bg-white text-[#07539A] hover:border-[#00A2D4] text-[12px] font-medium flex items-center gap-1.5 shadow-[0_4px_12px_rgba(7,83,154,0.06)]">
                <Plus size={13} /> Add company
              </button>
              )}
              <div className="relative">
                <button type="button" onClick={() => setFilterOpen(open => !open)} data-testid="universe-filter-btn"
                        className={`h-9 px-3 rounded-lg border text-[12px] font-medium flex items-center gap-2 shadow-[0_4px_12px_rgba(7,83,154,0.06)] transition-colors ${filterOpen || activeFilterCount ? "border-[#07539A] bg-[#E3F2F8] text-[#07539A]" : "border-[#BFD6E1] bg-white text-[#243B53] hover:border-[#07539A]"}`}>
                  <Filter size={14} /> Filter by
                  {activeFilterCount > 0 && <span className="w-5 h-5 rounded-full bg-[#07539A] !text-white text-[10px] flex items-center justify-center">{activeFilterCount}</span>}
                </button>
                {filterOpen && (
                  <div className="absolute right-0 top-11 z-30 w-[min(88vw,34rem)] rounded-2xl border border-[#C9DCE6] bg-white shadow-[0_20px_50px_rgba(7,83,154,0.20)] p-4" data-testid="universe-filter-panel">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="font-display text-lg text-[#07539A]">Filter companies</div>
                        <div className="text-[10px] text-[#78909C] mt-0.5">Combine filters to narrow the Universe.</div>
                      </div>
                      <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters" className="w-8 h-8 rounded-full hover:bg-[#EDF7FB] text-[#607D8B] flex items-center justify-center"><X size={14} /></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 [&_select]:w-full [&_select]:h-10 [&_select]:rounded-xl [&_select]:border [&_select]:border-[#B8D3DF] [&_select]:bg-[#F8FCFE] [&_select]:px-3 [&_select]:text-[12px] [&_select]:outline-none [&_select:focus]:border-[#00A2D4]">
                      <FilterSelect label="Company size" value={sizeFilter} onChange={setSizeFilter} options={SIZE_TABS} />
                      <FilterSelect label="Pipeline status" value={statusFilter} onChange={setStatusFilter} options={STATUS_TABS} />
                      <FilterSelect label="Partner" value={partnerFilter} onChange={setPartnerFilter} options={[
                        { key: "all", label: "All partners" }, { key: "assigned", label: "Assigned" }, { key: "unassigned", label: "Unassigned" },
                        ...partners.map(p => ({ key: p.id, label: p.firm || p.name })),
                      ]} />
                      <FilterSelect label="Fit to Ergoniq" value={fitFilter} onChange={setFitFilter} options={[
                        { key: "all", label: "All fit scores" }, { key: "good", label: "Good fit · 85+" },
                        { key: "mid", label: "Mid fit · 70–84" }, { key: "poor", label: "Low fit · below 70" },
                      ]} />
                      <FilterSelect label="Industry" value={industryFilter} onChange={setIndustryFilter} options={[
                        { key: "all", label: "All industries" }, ...industries.map(value => ({ key: value, label: value })),
                      ]} />
                    </div>
                    <div className="mt-4 pt-3 border-t border-[#E1EBF0] flex items-center justify-between">
                      <button type="button" onClick={clearFilters} className="text-[11px] text-[#607D8B] hover:text-[#07539A]">Clear all</button>
                      <button type="button" onClick={() => setFilterOpen(false)} className="h-9 px-4 rounded-xl bg-[#07539A] text-[#FFFFFF] text-[12px] font-medium">Show {total.toLocaleString()} companies</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        />

        <div className="border border-[#C9DCE6] rounded-[22px] bg-white overflow-x-auto shadow-[0_10px_28px_rgba(7,83,154,0.08)]" data-testid="universe-table-scroll">
          <div className="grid w-full min-w-[1110px] grid-cols-[32px_minmax(150px,2fr)_repeat(7,minmax(80px,1fr))_18px] gap-3 items-center px-4 py-3 bg-[#E7F5FB] border-b border-[#C9DCE6] text-[11px] font-semibold text-[#07539A]">
            <div className="col-span-2">Company</div>
            <div className="text-center">Industry</div>
            <div className="text-center">Size</div>
            <div className="text-center">Revenue ({MONEY_UNIT_SHORT})</div>
            <div className="text-center">No. of plants</div>
            <div className="text-center">Fit to Ergoniq</div>
            <div className="text-center">Current status</div>
            <div className="text-center">Partner</div>
            <div />
          </div>
          {loading ? (
          <div className="p-8 text-center text-[var(--text-muted)]">Loading universe…</div>
        ) : (
          <div className="w-full min-w-[1110px] divide-y divide-black/10">
            {companies.map(c => {
              const isExp = expanded === c.id;
              return (
                <div key={c.id} id={`company-row-${c.id}`} data-testid={`company-row-${c.id}`}>
                  <button onClick={() => setExpanded(isExp ? null : c.id)} className="w-full overflow-hidden text-left px-4 py-4 hover:bg-[#F8FCFE] transition-colors group" data-testid={`company-expand-${c.id}`}>
                    <div className="grid grid-cols-[32px_minmax(150px,2fr)_repeat(7,minmax(80px,1fr))_18px] gap-3 items-center">
                      <div className="w-8 h-8 rounded-sm bg-black/[0.05] border border-black/10 flex items-center justify-center text-[12px] text-[#526D7A] shrink-0">
                        {c.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[12px] font-medium text-[#526D7A] truncate">{c.name}</div>
                        <div className="text-[12px] text-[#526D7A] truncate">{c.hq_city || "Location unknown"}</div>
                      </div>
                      <div className="text-center text-[12px] text-[#526D7A]">{c.industry}</div>
                      <div className="text-center text-[12px] text-[#526D7A]">{SIZE_LABEL[c.size_category || "unknown"]}</div>
                      <div className="text-center text-[12px] text-[#526D7A] tabular-nums">{formatLakh(c.revenue_cr)}</div>
                      <div className="text-center text-[12px] text-[#526D7A] tabular-nums">{c.plants ?? "—"} plants</div>
                      <div className="flex items-center justify-center gap-2" title="AI-scored fit to Ergoniq · green good, orange mid, red low">
                        <ICPBar score={c.icp_score || 0} />
                        {canEditUniverse && (
                        <button onClick={(e) => { e.stopPropagation(); rescore(c.id); }} disabled={scoring === c.id} data-testid={`rescore-${c.id}`}
                                className="opacity-0 group-hover:opacity-100 text-[var(--copper)]" title="Re-score with AI">
                          {scoring === c.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                        </button>
                        )}
                      </div>
                      <div className="text-center">
                        <span className="text-[12px] text-[#526D7A]">{titleCase(c.status)}</span>
                      </div>
                      <div
                        className="flex items-center justify-center min-w-0 overflow-hidden [&_span]:text-[12px] [&_span]:font-normal [&_span]:text-[#526D7A] [&_button]:text-[12px] [&_button]:text-[#526D7A] [&_[data-testid='partner-assignment-compact']]:justify-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <PartnerAssignmentPanel entityType="company" entityId={c.id} compact showActions={false} />
                      </div>
                      <div className="text-[var(--text-dim)]">
                        {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>
                    </div>
                  </button>
                  {isExp && (
                    <div className="sticky left-0 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] md:static md:w-auto md:max-w-none md:mx-2 mb-3 px-3 md:px-4 py-4 bg-[#EAF7FC] border border-[#BBD8E7] rounded-[22px] space-y-4 shadow-[0_10px_24px_rgba(7,83,154,0.07)]" data-testid={`company-detail-${c.id}`}>
                      {/* Cross-stage customer view */}
                      <button
                        onClick={(e) => { e.stopPropagation(); setCustomerCid(c.id); }}
                        data-testid={`customer-360-${c.id}`}
                        className="w-full min-h-11 px-4 rounded-[16px] bg-[#07539A] hover:bg-[#063F73] !text-white text-[12px] font-medium flex items-center justify-center gap-2 shadow-[0_7px_18px_rgba(7,83,154,0.16)]"
                      >
                        <Layers size={12} /> Open Customer 360 — All history across Signals · Leads · Opps · Orders
                      </button>
                      <EditableField
                        label="Company name"
                        value={c.name}
                        testId={`company-name-${c.id}`}
                        readOnly={!canEditUniverse}
                        className="rounded-[18px] border-[#C9DCE6] bg-white shadow-[0_6px_18px_rgba(7,83,154,0.05)]"
                        onSave={async (name) => {
                          const { data } = await api.patch(`/companies/${c.id}`, { name: name.trim() });
                          patchCompany(c.id, { name: name.trim() });
                          showDownstreamSync(data.downstream_sync);
                          toast.success("Company name updated");
                        }}
                      />
                      {/* Actions + AI insight */}
                      <ActionEditor
                        actions={c.universe_actions || []}
                        aiInsights={c.ai_summary}
                        title="Universe actions"
                        accent="navy"
                        rounded
                        readOnly={!canEditUniverse}
                        onSave={async (arr) => {
                          const { data } = await api.patch(`/companies/${c.id}`, { universe_actions: arr });
                          patchCompany(c.id, { universe_actions: arr });
                          return data;
                        }}
                        onRegenerate={async () => {
                          const { data } = await api.post(`/companies/${c.id}/score-icp`);
                          patchCompany(c.id, { icp_score: data.icp_score, ai_summary: data.ai_summary });
                          return { ai_summary: data.ai_summary };
                        }}
                      />
                      {/* Partner assignment + Meeting log */}
                      <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.05)] [&>[data-testid='partner-assignment']]:border-0">
                        <PartnerAssignmentPanel entityType="company" entityId={c.id} accent="navy" />
                      </div>
                      <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.05)] [&>[data-testid='meeting-log']]:border-0">
                        <MeetingLogInline entityType="company" entityId={c.id} accent="navy" readOnly={!canLogMeetings} />
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-1">
                        {/* State-aware: send to Signals OR view existing Signal */}
                        {c.promoted_signal_id ? (
                          <Link
                            to="/signals"
                            data-testid={`view-signal-${c.id}`}
                            className="h-9 px-4 rounded-xl bg-[var(--success)]/10 border border-[var(--success)]/40 text-[var(--success)] text-[12px] font-medium flex items-center justify-center gap-2 hover:bg-[var(--success)]/15"
                          >
                            ✓ Signal already sent · View Signal →
                          </Link>
                        ) : canCreateSignal && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await api.post(`/universe/${c.id}/create-signal`, { intent: `Prospecting on ${c.name}`, strength: 65 });
                              toast.success("Signal created — jump to Signals to see it");
                              load({ silent: true });
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Could not create signal");
                            }
                          }}
                          data-testid={`send-to-signal-${c.id}`}
                          className="h-9 px-4 rounded-xl bg-[#E28A23] hover:bg-[#C97215] !text-white text-[12px] font-medium flex items-center justify-center gap-2 shadow-[0_8px_20px_rgba(226,138,35,0.28)] hover:shadow-[0_10px_24px_rgba(226,138,35,0.34)] transition-all"
                        >
                          <Sparkles size={12} /> Send to Signals
                        </button>
                        )}
                        {canDeleteCompany && (
                        <button
                          type="button"
                          onClick={() => deleteCompany(c.id, c.name)}
                          data-testid={`delete-company-${c.id}`}
                          className="text-[11px] px-3 h-9 rounded-xl bg-white border border-[var(--danger)]/30 text-[var(--danger)] hover:bg-[var(--danger)]/5 flex items-center gap-1"
                        >
                          <Trash2 size={11} /> Delete company
                        </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {!loading && hasMore && (
          <div className="p-4 border-t border-[#E1EBF0] text-center">
            <button
              type="button"
              onClick={() => load({ append: true, silent: true })}
              disabled={loadingMore}
              data-testid="universe-load-more"
              className="h-10 px-5 rounded-xl border border-[#07539A] text-[#07539A] text-[12px] font-medium hover:bg-[#E7F5FB] disabled:opacity-50"
            >
              {loadingMore ? "Loading…" : `Load more (${companies.length.toLocaleString()} of ${total.toLocaleString()})`}
            </button>
          </div>
        )}
      </div>

      </div>

      <AddCompanyModal open={addOpen} onOpenChange={setAddOpen} onAdded={load} />
      {customerCid && <CustomerCard companyId={customerCid} onClose={() => setCustomerCid(null)} />}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-[#455A64] mb-1.5">{label}</span>
      <select value={value} onChange={event => onChange(event.target.value)}>
        {options.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
      </select>
    </label>
  );
}
